import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Organization} from "app/models/organization.model";
import {TagDto} from "app/models/menu.model";
import {MatDialog} from "@angular/material/dialog";
import {Store} from "@ngrx/store";
import {TagService} from "app/services/tag.service";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {setTags} from "app/reducers/menu.actions";
import {
  EditTagModalComponent,
  EditTagModalComponentDataType
} from "app/pages/menu/shared/edit-tag-modal/edit-tag-modal.component";

@UntilDestroy()
@Component({
  selector: 'app-tags-form-field',
  templateUrl: './tags-form-field.component.html',
  styleUrls: ['./tags-form-field.component.scss']
})
export class TagsFormFieldComponent implements OnInit {
  @Input() tagIds: string[];
  @Input() organization: Organization;
  @Input() menuSectionId: string;
  @Input() menuId: string;
  @Input() allowOverrides: boolean;

  @Output() tagIdsChange = new EventEmitter<string[]>();

  initValues = {};

  tagDtos: TagDto[] = [];
  tagNames: Set<string> = new Set();

  tagIdSet: Set<string> = new Set();

  tagDtosMap: Map<string, TagDto>;
  originalTagDtosMap: Map<string, TagDto>;

  constructor(
    private tagSerivce: TagService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private store: Store,
  ) {
  }

  ngOnInit(): void {
    this.tagIdSet = new Set(this.tagIds);
    this.store.select(MenuSelectors.selectTags)
      .pipe(untilDestroyed(this)).subscribe(tags => {
      this.tagDtos = [...tags].sort((a, b) => a.name.localeCompare(b.name));
      this.tagDtosMap = new Map();
      for (const tag of tags) {
        this.tagDtosMap.set(tag.id, tag);
      }
    });
    this.store.select(MenuSelectors.selectDictionaryTags)
      .pipe(untilDestroyed(this)).subscribe(tags => {
      this.tagDtosMap = new Map();
      for (const kvp of tags) {
        this.tagDtosMap.set(kvp[0], kvp[1]);
      }
    });
    this.store.select(MenuSelectors.selectOriginalDictionaryTags)
      .pipe(untilDestroyed(this)).subscribe(originalTagsMap => {this.originalTagDtosMap = originalTagsMap});
    this.initValues['tags'] = this.tagIds?.map(x => x);
    for (const tagDto of this.tagDtos) {
      this.tagNames.add(tagDto.name);
    }
  }

  getTagName(tagId: string){
    return this.tagDtosMap?.get(tagId)?.name ?? tagId;
  }

  onRemoveTag(tagId: string){
    if(!this.allowOverrides){
      return;
    }
    if(this.tagIdSet.delete(tagId)){
      this.tagIdsChange.emit([...this.tagIdSet]);
    }
  }

  onTagPicked(tagId: string){
    if(!this.allowOverrides){
      return;
    }
    if(!this.tagIdSet.has(tagId)){
      this.tagIdSet.add(tagId);
      this.tagIdsChange.emit([...this.tagIdSet]);
    }
  }

  onOpenTag(tagId?: string) {
    if(!this.allowOverrides){
      return;
    }
    if(tagId && !this.getTagName(tagId)){
      return;
    }
    this.dialog.open(EditTagModalComponent, {
      data: {
        tagId: tagId,
        tag: tagId ? this.tagDtosMap.get(tagId) as TagDto : {isManuallyCreated: true} as TagDto,
        originalTag: this.originalTagDtosMap.get(tagId) ?? this.tagDtosMap.get(tagId) ?? {},
        organization: this.organization,
        menuSectionId: this.menuSectionId,
        menuId: this.menuId,
        tagNames: this.tagNames,
      } as EditTagModalComponentDataType
    },).afterClosed().subscribe(result => {
      if(result.created){
        const created = result.created as TagDto;
        this.tagDtosMap.set(created.id, created)
        this.store.dispatch(setTags([...this.tagDtosMap.values()]));
        this.tagNames.add(created.name)
        this.onTagPicked(created.id);
      } else if(result.updated){
        const updated = result.updated as TagDto;
        this.tagDtosMap.set(updated.id, updated);
        this.store.dispatch(setTags([...this.tagDtosMap.values()]));
      } else if(result.deleted){
        const deletedId = result.deleted as string;
        this.onRemoveTag(deletedId);
        this.deleteFromStore(deletedId);
      }
    });
  }

  async onDeleteTag(tagId: string){
    if(!this.allowOverrides){
      return;
    }
    if(confirm(this.translate.instant('MENU.DISHES.NEW.CONFIRM_DELETE_TAG'))){
      this.onRemoveTag(tagId);
      await this.tagSerivce.delete(tagId);
      this.deleteFromStore(tagId);
    }
  }

  isManuallyCreated(tagId: string) : boolean {
    const selected = this.tagDtosMap.get(tagId);
    return selected?.isManuallyCreated;
  }

  private deleteFromStore(tagId: string) {
    const filtered = [...this.tagDtos.filter(tag => tag.id !== tagId)];
    this.store.dispatch(setTags(filtered));
  }
}
